import { useQuery } from '@tanstack/react-query';
import { listStaffRoles } from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { Plus, Trash2 } from 'lucide-react';

export interface RoleRequirementRow {
  role_id: string;
  count: number;
}

interface RoleRequirementsEditorProps {
  value: RoleRequirementRow[];
  onChange: (rows: RoleRequirementRow[]) => void;
  /** Helper text shown under the editor (e.g. explaining count = 0). */
  hint?: string;
}

/**
 * Editor for per-stage default requirements and per-happening requirements:
 * rows of { role, count }. count = 0 is allowed and means "explicitly none".
 */
export function RoleRequirementsEditor({ value, onChange, hint }: RoleRequirementsEditorProps) {
  const { data: roles = [] } = useQuery({
    queryKey: ['staff-roles'],
    queryFn: () => listStaffRoles(),
  });

  const usedRoleIds = new Set(value.map((r) => r.role_id));
  const availableRoles = roles.filter((r) => !usedRoleIds.has(r.id));

  const addRow = () => {
    const next = availableRoles[0];
    if (!next) return;
    onChange([...value, { role_id: next.id, count: 1 }]);
  };

  const updateRow = (index: number, patch: Partial<RoleRequirementRow>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  if (roles.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No roles defined yet. Add some under Settings → Staff Roles.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {value.length === 0 && (
        <p className="text-sm text-muted-foreground">No role requirements.</p>
      )}
      {value.map((row, index) => {
        // The role currently on this row plus roles not used by other rows.
        const selectableRoles = roles.filter(
          (r) => r.id === row.role_id || !usedRoleIds.has(r.id),
        );
        return (
          <div key={index} className="flex items-center gap-2">
            <select
              value={row.role_id}
              onChange={(e) => updateRow(index, { role_id: e.target.value })}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {selectableRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <NumberInput
              min={0}
              allowEmpty={false}
              value={row.count}
              onChange={(n) => updateRow(index, { count: Math.max(0, n ?? 0) })}
              className="w-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <Button type="button" size="sm" variant="ghost" onClick={() => removeRow(index)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      })}
      <div className="flex items-center justify-between">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addRow}
          disabled={availableRoles.length === 0}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add role
        </Button>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
